/**
 * Public: Membership renewal / purchase flow.
 *
 * Shown at the bottom of the /partners landing page below the tier grid so
 * businesses can pay online for their annual partnership. Fetches active
 * membership products and lets the buyer pick a tier, supply details, then
 * redirects to Stripe Checkout.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, X } from 'lucide-react';
import axios from 'axios';
import partnerApi from '../lib/partnerApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(pounds, currency = 'gbp') {
  const symbol = currency === 'gbp' ? '£' : currency.toUpperCase() + ' ';
  return `${symbol}${Number(pounds).toFixed(2)}`;
}

const TIER_RANK = { gold: 3, silver: 2, bronze: 1 };

export default function MembershipRenewalModal({ open, onClose, title = 'Renew your partnership', aboveTier = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedLookupKey, setSelectedLookupKey] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [business, setBusiness] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setErr('');
    axios.get(`${API}/payments/products?category=membership`)
      .then(({ data }) => {
        let rows = data.items || [];
        if (aboveTier) rows = rows.filter((t) => (TIER_RANK[(t.tier || '').toLowerCase()] || 0) > (TIER_RANK[aboveTier] || 0));
        setItems(rows);
        if (rows.length && !selectedLookupKey) setSelectedLookupKey(rows[0].lookup_key);
      })
      .catch((e) => setErr(e.response?.data?.detail || 'Could not load membership options'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selected = items.find((t) => t.lookup_key === selectedLookupKey);

  async function checkout() {
    if (!selected) return;
    setSubmitting(true); setErr('');
    try {
      // Use partnerApi so the partner JWT is attached — backend then derives
      // partner_user_id from the token (never trusting the client).
      const { data } = await partnerApi.post('/payments/checkout', {
        lookup_key: selected.lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
        buyer_email: email || undefined,
        buyer_name: name || undefined,
        notes: business ? `Business: ${business}` : undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e.response?.data?.detail || 'Checkout failed. Please try again.');
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="membership-renewal-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-charcoal/10 px-6 py-4 flex items-center gap-3">
          <ShieldCheck className="text-heritage" size={22}/>
          <div className="flex-1">
            <div className="font-display text-xl">{title}</div>
            <div className="text-xs text-charcoal/60">Annual membership · secure Stripe checkout</div>
          </div>
          <button data-testid="membership-close" onClick={onClose} className="p-2 rounded-lg hover:bg-admin"><X size={20}/></button>
        </div>

        <div className="p-6 space-y-5">
          {loading && <div className="flex items-center gap-2 text-charcoal/60"><Loader2 className="animate-spin" size={16}/> Loading options…</div>}
          {err && <div data-testid="membership-error" className="p-3 rounded-xl bg-heritage/10 text-heritage text-sm">{err}</div>}

          {!loading && items.length === 0 && (
            <div className="text-charcoal/60" data-testid="membership-no-options">
              {aboveTier ? 'You\u2019re already on our top tier — thank you!' : <>Online renewals aren&apos;t set up yet. Please email <a className="text-heritage underline" href="mailto:janine.williams@l-p-l.com">janine.williams@l-p-l.com</a>.</>}
            </div>
          )}

          {items.length > 0 && (
            <>
              <fieldset className="space-y-2">
                <legend className="label text-charcoal/70">Choose a tier</legend>
                {items.map((t) => (
                  <label key={t.lookup_key} data-testid={`membership-option-${t.lookup_key}`}
                    className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${selectedLookupKey === t.lookup_key ? 'border-heritage bg-heritage/5' : 'border-charcoal/10 hover:border-charcoal/30'}`}>
                    <input type="radio" name="tier" checked={selectedLookupKey === t.lookup_key}
                      onChange={() => setSelectedLookupKey(t.lookup_key)}
                      className="mt-1"/>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium">{t.name}</span>
                        <span className="font-display text-lg text-heritage whitespace-nowrap">{money(t.amount, t.currency)}</span>
                      </div>
                      {t.description && <div className="text-sm text-charcoal/70 mt-0.5">{t.description}</div>}
                    </div>
                  </label>
                ))}
              </fieldset>

              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="label">Your name</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="membership-name-input"
                    className="mt-1 w-full border rounded-xl px-3 py-2"/>
                </label>
                <label className="block">
                  <span className="label">Email</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="membership-email-input"
                    className="mt-1 w-full border rounded-xl px-3 py-2"/>
                </label>
              </div>

              <label className="block">
                <span className="label">Business name</span>
                <input value={business} onChange={(e) => setBusiness(e.target.value)} data-testid="membership-business-input"
                  className="mt-1 w-full border rounded-xl px-3 py-2"/>
              </label>

              <div className="flex items-center justify-between pt-2 border-t border-charcoal/10">
                <div>
                  <div className="text-xs text-charcoal/60">Total (inc. VAT)</div>
                  <div className="font-display text-2xl">{money(selected?.amount || 0, selected?.currency || 'gbp')}</div>
                </div>
                <button data-testid="membership-checkout-btn" onClick={checkout} disabled={submitting || !selected}
                  className="btn-primary px-6 py-3 flex items-center gap-2">
                  {submitting ? <><Loader2 className="animate-spin" size={16}/> Redirecting…</> : 'Pay with Stripe'}
                </button>
              </div>

              <div className="text-xs text-charcoal/50">
                Stripe collects payment securely. You'll receive a VAT invoice by email — our accounts team reconciles via Xero.
                {' '}<span data-testid="membership-refund-policy">Membership packages are <strong>non-refundable</strong> — contact hello@goleicestershire.com with any queries.</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
